import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { transferApi, auditLogApi } from '../../api';
import { useAuth } from '../../context/AuthContext';
import StatusBadge from '../../components/common/StatusBadge';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmDialog from '../../components/common/ConfirmDialog';

export default function TransferDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isSiteRestricted, hasPermission } = useAuth();
  const [transfer, setTransfer] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false); // disables all action buttons while any one is in flight — prevents double-clicks firing two requests
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);

  const load = async () => {
    const [t, l] = await Promise.all([
      transferApi.getOne(id),
      auditLogApi.list({ entity: 'transfer', entityId: id }).catch(() => ({ data: { data: [] } })),
    ]);
    setTransfer(t.data.data);
    setLogs(l.data.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, [id]);

  const runAction = async (apiCall) => {
    setBusy(true);
    setError('');
    try {
      await apiCall();
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Action failed — the transfer may have already changed state. Refresh and try again.');
    } finally {
      setBusy(false);
    }
  };

  const doSend    = () => runAction(() => transferApi.send(id));
  const doReceive = () => runAction(() => transferApi.receive(id));
  const doApprove = () => runAction(() => transferApi.approve(id));
  const doReject  = () => setShowRejectConfirm(true);

  // Site-scoped, not permission-scoped — mirrors the backend check.
  const canSend    = transfer && (!isSiteRestricted || user?.siteId === transfer.fromSiteId);
  const canReceive = transfer && (!isSiteRestricted || user?.siteId === transfer.toSiteId);
  // Approval is permission-scoped (transfer_creation_approval), independent
  // of transfer_request — not site-scoped the way Send/Receive are.
  const canApprove = hasPermission('transfer_creation_approval');

  if (loading) return <LoadingSpinner />;
  if (!transfer) return <p className="text-gray-500">Transfer not found</p>;

  return (
    <div className="max-w-3xl">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/transfers')} className="text-gray-500 hover:text-gray-700 text-sm">← Back</button>
        <h1 className="text-2xl font-bold text-gray-900">Transfer #{transfer.id}</h1>
        <StatusBadge status={transfer.status} />
      </div>

      <div className="card mb-4">
        <dl className="grid grid-cols-2 gap-4 text-sm">
          <div><dt className="text-gray-500">From Site</dt><dd className="font-medium mt-0.5">{transfer.fromSite?.name} <span className="text-xs text-gray-400">({transfer.fromSite?.group?.name})</span></dd></div>
          <div><dt className="text-gray-500">To Site</dt><dd className="font-medium mt-0.5">{transfer.toSite?.name}</dd></div>
          <div><dt className="text-gray-500">Item</dt><dd className="font-medium mt-0.5">{transfer.kit?.name || transfer.item?.name || transfer.itItem?.name}</dd></div>
          <div><dt className="text-gray-500">Quantity</dt><dd className="font-bold text-lg mt-0.5">{transfer.quantity} {transfer.kit?.unit || transfer.item?.unit || transfer.itItem?.unit}</dd></div>
          <div><dt className="text-gray-500">Requested By</dt><dd className="mt-0.5">{transfer.requestedByUsername || '—'}</dd></div>
          {transfer.approvedByUsername && <div><dt className="text-gray-500">Approved By</dt><dd className="mt-0.5">{transfer.approvedByUsername}</dd></div>}
          <div><dt className="text-gray-500">Sent By</dt><dd className="mt-0.5">{transfer.sentByUsername || '—'}</dd></div>
          <div><dt className="text-gray-500">Received By</dt><dd className="mt-0.5">{transfer.receivedByUsername || '—'}</dd></div>
          {transfer.requirement && <div><dt className="text-gray-500">Linked Requirement</dt><dd className="mt-0.5">#{transfer.requirement.id}</dd></div>}
          {transfer.remarks && <div className="col-span-2"><dt className="text-gray-500">Remarks</dt><dd className="mt-0.5">{transfer.remarks}</dd></div>}
        </dl>
      </div>

      {error && <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">{error}</p>}

      <div className="flex gap-3 mb-6">
        {transfer.status === 'awaiting_approval' && canApprove && (
          <>
            <button className="btn-success disabled:opacity-50" disabled={busy} onClick={doApprove}>{busy ? 'Approving…' : 'Approve'}</button>
            <button className="btn-danger disabled:opacity-50" disabled={busy} onClick={doReject}>{busy ? 'Rejecting…' : 'Reject'}</button>
          </>
        )}
        {transfer.status === 'pending' && canSend && (
          <button className="btn-primary disabled:opacity-50" disabled={busy} onClick={doSend}>{busy ? 'Sending…' : 'Mark as Sent'}</button>
        )}
        {transfer.status === 'sent' && canReceive && (
          <button className="btn-success disabled:opacity-50" disabled={busy} onClick={doReceive}>{busy ? 'Receiving…' : 'Mark as Received'}</button>
        )}
      </div>

      {logs.length > 0 && (
        <div className="card">
          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Activity Timeline</h3>
          <div className="space-y-3">
            {logs.map((log) => (
              <div key={log.id} className="flex gap-3 text-sm">
                <div className="w-2 h-2 rounded-full bg-blue-400 mt-1.5 flex-shrink-0" />
                <div>
                  <span className="font-medium capitalize">{log.action.toLowerCase()}</span>
                  <span className="text-gray-500"> by {log.username || 'system'}</span>
                  <span className="text-gray-400 text-xs ml-2">{new Date(log.createdAt).toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={showRejectConfirm}
        title="Reject Transfer"
        message="Reject this transfer request?"
        confirmLabel="Reject"
        onConfirm={() => { setShowRejectConfirm(false); runAction(() => transferApi.cancel(id)); }}
        onCancel={() => setShowRejectConfirm(false)}
      />
    </div>
  );
}
